/**
 * Bike finance widget (Yonda / WesBank)
 *
 * This file does two jobs:
 *
 * 1. Converts the server-rendered total-to-finance amount (in cents) inside
 *    every ".fin_price" element into a formatted monthly instalment, e.g.
 *    "R1234.56pm". This covers every card the THEME renders with Liquid:
 *    the main product page, search results, and the default (unfiltered)
 *    collection grid.
 *
 * 2. Separately injects the whole widget for cards rendered by the Globo
 *    Filters & Search app once a filter/search is actually applied
 *    (".spf-product-card" markup). Those cards are built by Globo's own
 *    client-side script from its own remote template -- there is no theme
 *    file that controls them, so this widget can't be added there via
 *    Liquid at all. Instead this fetches each visible card's product tags
 *    via Shopify's public /products/<handle>.js endpoint and, if tagged
 *    "finance", builds and inserts the same widget markup/classes so the
 *    existing CSS (assets/custom.css) styles it identically.
 *
 * Fee/rate figures for part 2 are hardcoded below (mirroring
 * Settings > Bike finance calculator) because Globo's cards are outside
 * Liquid and can't read theme settings. If those theme settings change,
 * update FINANCE_CONFIG below to match.
 */
(function () {
  var YONDA_URL = 'https://www.yonda.co.za/store/eastcitycycles';
  var FINANCE_CONFIG = {
    wesbankFeePercent: 12.75,
    wesbankInitiationFeeCents: 120700,
    yondaFeeCents: 235000,
    adminFee: 68,
    loanPeriodMonths: 48
  };

  function pmt(monthlyRate, months, presentValue) {
    if (!months) return 0;
    if (!monthlyRate) return presentValue / months;
    var factor = Math.pow(1 + monthlyRate, months);
    return (presentValue * monthlyRate * factor) / (factor - 1);
  }

  /* ---- Part 1: format .fin_price elements the theme's Liquid already rendered ---- */

  function formatFinancePrice(el) {
    if (!el || el.dataset.eccFinanceFormatted === 'true') return;

    var totalCents = parseInt(el.textContent, 10);
    if (isNaN(totalCents)) return;

    var total = totalCents / 100;
    var annualRate = parseFloat(el.dataset.rate || '0') / 100;
    var months = parseInt(el.dataset.period || '0', 10);
    var adminFee = parseFloat(el.dataset.adminFee || '0');
    var monthlyRate = annualRate / 12;

    var payment = pmt(monthlyRate, months, total) + adminFee;

    el.textContent = 'R' + payment.toFixed(2) + 'pm';
    el.dataset.eccFinanceFormatted = 'true';
  }

  function formatAllWithin(root) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('.fin_price').forEach(formatFinancePrice);
  }

  /* ---- Part 2: inject the widget into Globo's own .spf-product-card markup ---- */

  var tagCache = {};

  function buildWidgetNode(priceCents) {
    var deposit = Math.floor(priceCents / 10);
    var priceToFinance = priceCents - deposit + FINANCE_CONFIG.wesbankInitiationFeeCents + FINANCE_CONFIG.yondaFeeCents;
    var monthlyRate = (FINANCE_CONFIG.wesbankFeePercent / 100) / 12;
    var payment = pmt(monthlyRate, FINANCE_CONFIG.loanPeriodMonths, priceToFinance / 100) + FINANCE_CONFIG.adminFee;

    var div = document.createElement('div');
    div.className = 'finance_price finance_price--card';
    div.innerHTML =
      '<a href="' + YONDA_URL + '" class="finance_price__link" target="_blank" rel="noopener">' +
        '<u>Finance</u> from ' +
        '<span class="fin_price" data-ecc-finance-formatted="true">R' + payment.toFixed(2) + 'pm</span> ' +
        '<span class="info_icon">' +
          '<svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true" focusable="false">' +
            '<circle cx="10" cy="10" r="9" fill="none" stroke="currentColor" stroke-width="1.4"></circle>' +
            '<rect x="9.1" y="8.5" width="1.8" height="6" fill="currentColor"></rect>' +
            '<rect x="9.1" y="5.3" width="1.8" height="1.8" fill="currentColor"></rect>' +
          '</svg>' +
          '<span class="info_popup"><ul>' +
            '<li>10% deposit</li><li>Over ' + FINANCE_CONFIG.loanPeriodMonths + ' months</li><li>Subject to approval</li><li>Ts &amp; Cs apply</li>' +
          '</ul></span>' +
        '</span>' +
      '</a>';
    return div;
  }

  function extractHandle(card) {
    var link = card.querySelector('a[href*="/products/"]');
    if (!link) return null;
    var match = (link.getAttribute('href') || '').match(/\/products\/([a-z0-9\-]+)/i);
    return match ? match[1] : null;
  }

  function insertWidget(card, priceCents) {
    if (card.querySelector('.finance_price')) return;
    var widget = buildWidgetNode(priceCents);
    var priceWrapper = card.querySelector('.spf-product-card__price-wrapper, .product-item-price');
    if (priceWrapper && priceWrapper.parentNode) {
      priceWrapper.parentNode.insertBefore(widget, priceWrapper.nextSibling);
    } else {
      card.appendChild(widget);
    }
  }

  function processGloboCard(card) {
    if (card.dataset.eccFinanceChecked === 'true') return;
    card.dataset.eccFinanceChecked = 'true';

    var handle = extractHandle(card);
    if (!handle) return;

    function apply(data) {
      if (!data || !data.price) return;
      var tagged = (data.tags || []).some(function (t) {
        return String(t).trim().toLowerCase() === 'finance';
      });
      if (tagged) insertWidget(card, data.price);
    }

    if (tagCache[handle]) {
      if (tagCache[handle].then) {
        tagCache[handle].then(apply);
      } else {
        apply(tagCache[handle]);
      }
      return;
    }

    var request = fetch('/products/' + handle + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        tagCache[handle] = data;
        apply(data);
        return data;
      })
      .catch(function () {});

    tagCache[handle] = request;
  }

  function scanGloboCards(root) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('.spf-product-card, article.product-item').forEach(processGloboCard);
  }

  /* ---- Wire both parts to initial load + dynamic content ---- */

  function scanAll(root) {
    formatAllWithin(root);
    scanGloboCards(root);
  }

  document.addEventListener('DOMContentLoaded', function () {
    scanAll(document);
  });

  if ('MutationObserver' in window) {
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('.fin_price')) {
            formatFinancePrice(node);
          }
          if (node.matches && node.matches('.spf-product-card, article.product-item')) {
            processGloboCard(node);
          }
          scanAll(node);
        });
      });
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }
})();
